import {
  AiAnalyticsEventType,
  AnalyticsData,
  AnalyticsDataWithTrends,
  DimensionValue,
} from 'common/src/modules/analytics/analytics.types';
import {AiDashboardWidgetType} from 'common/src/modules/dashboards/dashboards.types';
import {CopilotWidgetType} from 'common/src/modules/shared/copilot/copilot.enums';
import {Caller, ToolUtils} from 'common/src/modules/shared/copilot/copilot.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {z} from 'zod';
import {inlineWidgetSchema} from '../../../copilotAnalytics.schemas';
import generateInlineWidgetTitle from '../../campaignAnalyticsAgent/utils/generateInlineWidgetTitle';
import {doesUserHaveRole} from 'common/src/modules/roles/utils/doesUserHaveRole';
import {RoleTypeName} from 'common/src/modules/users/users.types';
import {users as usersShared} from '@cl/shared';
import appendRowCountFactsToToolResultMessage from '../../../utils/appendRowCountFactsToToolResultMessage';

export default async function persistInlineWidgetToAccountDashboard({
  toolUtils,
  callChain,
  title,
  params,
  requestedMaxRows,
}: {
  toolUtils: ToolUtils<Record<string, unknown>>;
  callChain: Caller[];
  title: string;
  requestedMaxRows?: number | null;
  params: {
    metrics: string[];
    dimensions: string[];
    startDate: string;
    endDate: string;
    type: AiDashboardWidgetType;
    analyticsData: (AnalyticsData | AnalyticsDataWithTrends)[];
    breakdownByTime: boolean;
    dimensionValues?: Record<string, {uiName: string; value: DimensionValue}>;
    dimensionFilterValues?: Record<string, string>[];
  };
}) {
  const userHasAnalyticsWriteRole = doesUserHaveRole(
    usersShared.userSubject.data,
    RoleTypeName.AnalyticsWrite,
  );

  const generatedTitle = await generateInlineWidgetTitle({
    invokeStandaloneLLMCall: toolUtils.invokeStandaloneLLMCall,
    callChain,
    fallbackTitle: title,
    widgetKindDescription: title,
    metrics: params.metrics,
    dimensions: params.dimensions,
    startDate: params.startDate,
    endDate: params.endDate,
  });

  const widgetPayload = {
    type: CopilotWidgetType.InlineWidget,
    content: {
      title: generatedTitle,
      metrics: params.metrics,
      dimensions: params.dimensions,
      type: params.type,
      analyticsData: params.analyticsData,
      breakdownByTime: params.breakdownByTime,
      id: 0,
      dimensionValues: params.dimensionValues ?? {},
      dimensionFilterValues: params.dimensionFilterValues ?? [],
      startDate: params.startDate,
      endDate: params.endDate,
      eventType: AiAnalyticsEventType.Adserving,
      withTrends: false,
    },
  } as z.infer<typeof inlineWidgetSchema>;

  toolUtils.showMessage(widgetPayload);

  if (!userHasAnalyticsWriteRole) {
    return toolResult(JSON.stringify(widgetPayload.content));
  }

  const rowCountFacts = {
    rowCount: params.analyticsData.length,
    requestedMaxRows,
  };

  return toolResult(appendRowCountFactsToToolResultMessage('Success', rowCountFacts));
}
